import { useState } from 'preact/hooks';
import { nameErrorKey, validateName } from '../core/name-validation';
import { t } from '../i18n';
import { AlertCircleIcon, BackIcon, CheckIcon, CloseIcon } from './Icons';

type Props = {
  onBack: () => void;
  onClose: () => void;
  onSubmit: (data: { name: string }) => void;
  initialName?: string;
  panelClass?: string;
  isMobile?: boolean;
};

// Временно скрыто пока PDF-ссылки на согласие лежат за корп-VPN.
// Включить → поставить true (форма снова потребует чекбокс).
const SHOW_CONSENT = false;

export function PreChatForm({
  onBack,
  onClose,
  onSubmit,
  initialName = '',
  panelClass = '',
  isMobile = false,
}: Props) {
  const [name, setName] = useState(initialName);
  const [consent, setConsent] = useState(true);
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameTouched, setNameTouched] = useState(false);

  const canSubmit = (!SHOW_CONSENT || consent) && validateName(name) === null;

  function resolveNameError(value: string): string | null {
    const validationError = validateName(value);
    return validationError ? t(nameErrorKey(validationError)) : null;
  }

  function handleNameInput(e: Event) {
    const next = (e.currentTarget as HTMLInputElement).value;
    setName(next);
    if (nameTouched) setNameError(resolveNameError(next));
  }

  function handleNameBlur() {
    setNameTouched(true);
    setNameError(resolveNameError(name));
  }

  function handleSubmit(e: Event) {
    e.preventDefault();
    setNameTouched(true);
    const nextNameErr = resolveNameError(name);
    setNameError(nextNameErr);
    if (nextNameErr || (SHOW_CONSENT && !consent)) return;
    onSubmit({ name: name.trim() });
  }

  const panelStyle = isMobile
    ? ({ width: '100%' } as const)
    : ({ width: 360 } as const);

  return (
    <div
      class={`sw-panel${panelClass ? ` ${panelClass}` : ''}`}
      style={panelStyle}
      role="dialog"
      aria-label={t('form.title')}
    >
      <div class="sw-header">
        <button type="button" class="sw-icon-btn" aria-label={t('common.back')} onClick={onBack}>
          <BackIcon />
        </button>
        <div style={{ flex: 1 }}>
          <div class="sw-header-title">{t('form.title')}</div>
        </div>
        <button type="button" class="sw-icon-btn" aria-label={t('common.close')} onClick={onClose}>
          <CloseIcon />
        </button>
      </div>

      <form class="sw-form" onSubmit={handleSubmit} noValidate>
        <div class="sw-form-body">
          <div class="sw-form-intro">{t('form.intro')}</div>

          <div class="sw-field">
            <label class="sw-label" for="sw-name">
              {t('form.name')}
              <span class="req">*</span>
            </label>
            <input
              id="sw-name"
              class={`sw-input${nameError ? ' error' : ''}`}
              placeholder={t('form.name_placeholder')}
              value={name}
              aria-invalid={nameError ? 'true' : 'false'}
              aria-describedby={nameError ? 'sw-name-err' : undefined}
              onInput={handleNameInput}
              onBlur={handleNameBlur}
            />
            {nameError ? (
              <div id="sw-name-err" class="sw-error-msg" role="alert">
                <AlertCircleIcon />
                {nameError}
              </div>
            ) : null}
          </div>
        </div>

        <div class="sw-form-footer">
          {SHOW_CONSENT ? (
            <label class={`sw-check${consent ? ' checked' : ''}`}>
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent((e.currentTarget as HTMLInputElement).checked)}
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
              />
              <span class="box">
                <CheckIcon />
              </span>
              <span>
                {t('form.consent_before')}
                <a
                  href={t('form.consent_url')}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('form.consent_link')}
                </a>
                {t('form.consent_after')}
              </span>
            </label>
          ) : null}

          <button type="submit" class="sw-btn" disabled={!canSubmit}>
            {t('form.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}
